import styled from "styled-components"
import React from "react"
import {Accordion, AccordionDetails, AccordionSummary, CircularProgress, Typography} from "@mui/material"
import {ExpandMore as ExpandMoreIcon, Phone as PhoneIcon} from "@mui/icons-material"

import {api, CustomerVoiceCallActivityPayload, VoiceTranscriptResponse} from "@indebted/api"

type VoiceCallActivityProps = {
	panelId: number
	activity: {Title: string; HappenedAt: string; Payload: CustomerVoiceCallActivityPayload}
	expanded: boolean
	handleExpand: (panelID: number) => (event: any, isExpanded: boolean) => void
	classes: {summary: string; activityContent: string; activityContentLabel: string; activityContentValue: string}
}

function VoiceCallActivity({panelId, activity, expanded, handleExpand, classes}: VoiceCallActivityProps) {
	const {Title, HappenedAt, Payload} = activity
	const [transcriptExpanded, setTranscriptExpanded] = React.useState(false)
	const [transcript, setTranscript] = React.useState<VoiceTranscriptResponse | null>(null)
	const [transcriptLoading, setTranscriptLoading] = React.useState(false)
	const [transcriptError, setTranscriptError] = React.useState<string | null>(null)

	const transcriptRegionID = `voice-call-transcript-${panelId}`
	const transcriptURI = Payload.TranscriptURI

	React.useEffect(() => {
		if (!transcriptExpanded || transcript !== null) {
			return
		}
		let cancelled = false
		setTranscriptLoading(true)
		setTranscriptError(null)
		api.customer
			.voiceTranscript(transcriptURI)
			.then((response) => {
				if (!cancelled) {
					setTranscript(response)
					setTranscriptLoading(false)
				}
			})
			.catch((error) => {
				if (!cancelled) {
					setTranscriptError(error?.Message ?? "An unexpected error occurred")
					setTranscriptLoading(false)
				}
			})
		return () => {
			cancelled = true
		}
	}, [transcriptExpanded, transcript, transcriptURI])

	const toggleTranscript = () => {
		setTranscriptExpanded((show) => !show)
	}

	const renderTranscript = () => {
		if (transcriptLoading) {
			return <CircularProgress size={16} />
		}
		if (transcriptError) {
			return (
				<Typography className={classes.activityContentValue}>
					Failed to load transcript: {transcriptError}
				</Typography>
			)
		}
		if (!transcript) {
			return null
		}
		return (
			<div>
				{transcript.segments.map((segment, index) => (
					<div key={`${segment.startSeconds}-${segment.speaker}-${index}`}>
						<Typography className={classes.activityContentLabel}>
							{speakerLabel(segment.speaker)}{" "}
							<StyledTimestamp>{formatDuration(segment.startSeconds)}</StyledTimestamp>
						</Typography>
						<Typography className={classes.activityContentValue}>{segment.text}</Typography>
					</div>
				))}
			</div>
		)
	}

	return (
		<Accordion expanded={expanded} onChange={handleExpand(panelId)}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<PhoneIcon color="primary" />
				<Typography className={classes.summary}>
					{" "}
					<b>{Title}</b> - {HappenedAt}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{expanded && (
					<div className={classes.activityContent}>
						<div>
							<Typography className={classes.activityContentLabel}>Direction:</Typography>
							<Typography className={classes.activityContentValue}>{Payload.Direction}</Typography>
						</div>
						<div>
							<Typography className={classes.activityContentLabel}>Agent:</Typography>
							<Typography className={classes.activityContentValue}>{Payload.AgentName}</Typography>
						</div>
						<div>
							<Typography className={classes.activityContentLabel}>Queue:</Typography>
							<Typography className={classes.activityContentValue}>{Payload.QueueName}</Typography>
						</div>
						<div>
							<Typography className={classes.activityContentLabel}>Duration:</Typography>
							<Typography className={classes.activityContentValue}>
								{formatDuration(Payload.DurationSeconds)}
							</Typography>
						</div>
						<div>
							<Typography className={classes.activityContentLabel}>Wrap-up:</Typography>
							<Typography className={classes.activityContentValue}>
								<b>{Payload.WrapupCodeLabel}</b>
							</Typography>
						</div>
						{Payload.WrapupNote && (
							<div>
								<Typography className={classes.activityContentLabel}>Wrap-up Note:</Typography>
								<Typography className={classes.activityContentValue}>{Payload.WrapupNote}</Typography>
							</div>
						)}
						{Payload.HasTranscript ? (
							<div>
								<StyledTranscriptButton
									onClick={toggleTranscript}
									aria-expanded={transcriptExpanded}
									aria-controls={transcriptRegionID}
								>
									<Typography variant="body2" component="span">
										{transcriptExpanded ? "Hide transcript" : "Show transcript"}
									</Typography>
								</StyledTranscriptButton>
								{transcriptExpanded && <div id={transcriptRegionID}>{renderTranscript()}</div>}
							</div>
						) : (
							<Typography className={classes.activityContentValue}>No transcript</Typography>
						)}
					</div>
				)}
			</AccordionDetails>
		</Accordion>
	)
}

function speakerLabel(speaker: string) {
	if (speaker === "agent") {
		return "Agent"
	}
	if (speaker === "customer") {
		return "Customer"
	}
	const speakerIndex = /^spk_(\d+)$/.exec(speaker)
	return speakerIndex ? `Speaker ${Number(speakerIndex[1]) + 1}` : "Speaker"
}

function formatDuration(totalSeconds: number) {
	const seconds = Math.floor(totalSeconds)
	const h = Math.floor(seconds / 3600)
	const m = Math.floor((seconds % 3600) / 60)
	const s = seconds % 60
	if (h > 0) {
		return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
	}
	return `${m}:${String(s).padStart(2, "0")}`
}

const StyledTimestamp = styled.span`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.38);
`

const StyledTranscriptButton = styled.button`
	display: flex;
	align-items: center;
	border: none;
	background: none;
	cursor: pointer;
	padding: 8px 12px 8px 0;
	border-radius: 4px;
	color: #1976d2;
	transition: background-color 0.2s ease;

	&:hover {
		background-color: rgba(25, 118, 210, 0.08);
	}

	&:focus {
		outline: 2px solid #1976d2;
		outline-offset: 2px;
	}
`

export {VoiceCallActivity}
